import {
  arrayPush,
  arrayRemoveAll,
  change,
  formValueSelector,
} from 'redux-form';
import uuid from 'uuid/v4';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { logger } from 'ks-logger';
import * as common from './sagasCommon';
import * as apiCommon from '../services/apiCommon';
import { FIND_MATCH_FOR_ITEM_VALUE } from '../constants/constant_saga_types';
import { OPEN } from '../constants/constant_transaction_status';
import {
  InventoryAdjustmentForm,
  InventoryTransferForm,
  InventoryReturnForm,
  PurchaseOrderForm,
  PurchaseReceiptForm,
  InventoryStocktakeForm,
} from '../constants/constant_form_names';
import { getDefaultLineStatus } from '../components/admin/inventory_management/adjustment/InventoryAdjustmentSelector';
import {
  getPurchaseOrderLineItemLocation,
  getPurchaseReceiptLineItemLocation,
  getInventoryReturnLineItemLocation,
} from '../actions/actionCreatorsPurchase';
import {
  getAdjustmentLineItemLocation,
  getInventoryTransferLineItemLocation,
} from '../actions/actionCreatorsInventory';
import { forceShowResults } from '../actions/actionCreatorGlobalSearch';
import { getMatchedScanItemIndex } from '../components/admin/inventory_management/stocktake/InventoryStocktakeSelector';

const console = logger('@saga_barcode');
export function* findMatchItemValue(feathersApp) {
  yield takeLatest(
    FIND_MATCH_FOR_ITEM_VALUE,
    callFindMatchItemValue,
    feathersApp
  );
}

function* callFindMatchItemValue(app, action) {
  try {
    const { path } = yield select();
    const { value } = action;
    const result = yield call(apiCommon.queryDocs, app, 'item/value', {
      value,
    });
    let validInventory = false;
    if ((path || '').indexOf('inventory/adjustment') > -1) {
      validInventory = yield call(
        handleInventory,
        InventoryAdjustmentForm,
        value,
        result
      );
    } else if ((path || '').indexOf('inventory/transfer') > -1) {
      validInventory = yield call(
        handleInventory,
        InventoryTransferForm,
        value,
        result,
        'from_location_id'
      );
    } else if ((path || '').indexOf('purchase/return') > -1) {
      validInventory = yield call(
        handleInventory,
        InventoryReturnForm,
        value,
        result
      );
    } else if ((path || '').indexOf('inventory/stocktake') > -1) {
      validInventory = yield call(
        handleInventory,
        InventoryStocktakeForm,
        value,
        result
      );
    } else if ((path || '').indexOf('purchase/receipt') > -1) {
      validInventory = yield call(
        handleInventory,
        PurchaseReceiptForm,
        value,
        result
      );
    } else if ((path || '').indexOf('purchase/order') > -1) {
      validInventory = yield call(
        handleInventory,
        PurchaseOrderForm,
        value,
        result
      );
    }

    if (!validInventory && value) {
      const input = document.querySelector('#GlobalSearch');
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      ).set;
      nativeInputValueSetter.call(input, value);
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);
      yield put(forceShowResults(true));
    }
  } catch (e) {
    yield common.showErrorSaveRecord(e);
  }
}

function* handleInventory(form, barcode, item, srcLocation = 'location_id') {
  const selector = formValueSelector(form);
  const transactionStatus = yield select(selector, 'status');
  if (!transactionStatus || transactionStatus.cid !== OPEN) {
    return false;
  }
  const defaultLineStatus = yield select(getDefaultLineStatus);
  const itemUom = yield call(getItemUom, item, barcode);
  const itemObj = {
    item,
    item_id: itemUom ? `${item.id}:${itemUom.id}` : item.id,
    item_type_id: item.item_type,
    item_type: item.item_type,
    item_uom: itemUom,
    item_uom_id: (itemUom || {}).id,
  };
  const status = {
    status: defaultLineStatus,
    status_id: defaultLineStatus.id,
  };
  const locationId = yield select(selector, srcLocation);
  if (!locationId) {
    // assume that if no location, default to global search
    return false;
  }
  if (form !== InventoryStocktakeForm) {
    yield put(
      arrayPush(form, 'lines', { ...itemObj, ...status, line_id: uuid() })
    );
  }
  const lines = yield select(selector, 'lines');
  const index = lines.length - 1;
  if (!item) {
    console.warn('no item found');
    return false;
  }
  switch (form) {
    case InventoryStocktakeForm: {
      yield put(
        arrayRemoveAll('InventoryStocktakeFormLinesFilter', 'item_ids')
      );
      yield put(change(form, 'matched_scanned_item', item));
      yield put(
        arrayPush('InventoryStocktakeFormLinesFilter', 'item_ids', item.id)
      );
      const itemIndex = yield select(getMatchedScanItemIndex);
      yield put(change(form, `lines[${itemIndex}].onEditMode`, true));
      break;
    }
    case InventoryAdjustmentForm: {
      yield put(
        getAdjustmentLineItemLocation(locationId, item.id, index, itemUom)
      );
      break;
    }
    case InventoryTransferForm: {
      yield put(
        getInventoryTransferLineItemLocation(
          locationId,
          item.id,
          index,
          itemUom
        )
      );
      break;
    }
    case PurchaseOrderForm: {
      yield put(
        getPurchaseOrderLineItemLocation(locationId, item.id, index, itemUom)
      );
      break;
    }
    case PurchaseReceiptForm: {
      yield put(
        getPurchaseReceiptLineItemLocation(locationId, item.id, index, itemUom)
      );
      break;
    }
    case InventoryReturnForm: {
      yield put(
        getInventoryReturnLineItemLocation(locationId, item.id, index, itemUom)
      );
      break;
    }
    default:
  }
  return true;
}

function getItemUom(item, barcodeVal) {
  if (item) {
    const itemBarcode = (item.barcodes || []).find(
      ({ barcode }) => barcode === barcodeVal
    );
    const itemQrcode = (item.qrcodes || []).find(
      ({ qrcode }) => qrcode === barcodeVal
    );
    return (itemBarcode || itemQrcode || {})['item_uom'];
  }
  return null;
}
